import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, Alert,
  CircularProgress, Chip, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  PersonAdd as RegisterIcon,
  Person as PersonIcon,
  CheckCircle as MatchedIcon,
  Error as NotFoundIcon,
} from '@mui/icons-material';
import { companyRelationshipApi, CompanyRelationship, RequestedUser } from 'shared/services/companyRelationshipApi';
import { userApi } from 'shared/services/userApi';

interface ReviewInvitationDialogProps {
  open: boolean;
  onClose: () => void;
  invitation: CompanyRelationship | null;
  providerCompanyId: string;
  onSuccess?: () => void;
}

interface UserMatch {
  cedula: string;
  found: boolean;
  user?: { _id: string; firstName: string; lastName: string; email?: string };
}

export const ReviewInvitationDialog: React.FC<ReviewInvitationDialogProps> = ({
  open, onClose, invitation, providerCompanyId, onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState<number | null>(null);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<Record<string, UserMatch>>({});
  const [currentInvitation, setCurrentInvitation] = useState<CompanyRelationship | null>(null);

  // Load provider users and match by cédula
  const loadMatches = useCallback(async () => {
    if (!invitation?.requestedUsers?.length) return;
    setLoading(true);
    try {
      const res = await userApi.getSimple({ company: providerCompanyId, limit: 200 });
      const companyUsers = res?.data || [];

      const matchMap: Record<string, UserMatch> = {};
      for (const ru of invitation.requestedUsers) {
        const match = companyUsers.find((u: any) =>
          u.cedula && ru.cedula && u.cedula.replace(/\s/g, '') === ru.cedula.replace(/\s/g, '')
        );
        matchMap[ru.cedula] = match
          ? { cedula: ru.cedula, found: true, user: { _id: match._id, firstName: match.firstName, lastName: match.lastName, email: match.email } }
          : { cedula: ru.cedula, found: false };
      }
      setMatches(matchMap);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, [invitation, providerCompanyId]);

  useEffect(() => {
    if (open && invitation) {
      setCurrentInvitation(invitation);
      setError(null);
      loadMatches();
    }
  }, [open, invitation, loadMatches]);

  const handleRespondUser = async (idx: number, action: 'approve' | 'reject') => {
    if (!currentInvitation) return;
    setResponding(idx);
    setError(null);
    try {
      const updated = await companyRelationshipApi.respondToUser(currentInvitation._id, idx, action);
      setCurrentInvitation(updated);
    } catch (err: any) {
      setError(err.message || 'Error al responder');
    } finally {
      setResponding(null);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!currentInvitation) return;
    setAcceptingAll(true);
    setError(null);
    try {
      await companyRelationshipApi.respond(currentInvitation._id, 'accept');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al aceptar invitación');
    } finally {
      setAcceptingAll(false);
    }
  };

  const handleRejectInvitation = async () => {
    if (!currentInvitation) return;
    setAcceptingAll(true);
    setError(null);
    try {
      await companyRelationshipApi.respond(currentInvitation._id, 'reject');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al rechazar invitación');
    } finally {
      setAcceptingAll(false);
    }
  };

  const clientName = currentInvitation && typeof currentInvitation.clientCompany === 'object'
    ? currentInvitation.clientCompany.name : 'Empresa';
  const reqUsers = currentInvitation?.requestedUsers || [];
  const allProcessed = reqUsers.length > 0 && reqUsers.every(u => u.status !== 'pending');

  const statusLabels: Record<string, string> = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado', substituted: 'Sustituido' };
  const statusColors: Record<string, 'warning' | 'success' | 'error' | 'info'> = { pending: 'warning', approved: 'success', rejected: 'error', substituted: 'info' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography fontWeight={600}>Revisar Invitación</Typography>
          <Typography variant="caption" color="text.secondary">{clientName}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
        ) : reqUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No hay personas solicitadas en esta invitación.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {clientName} solicita las siguientes personas. Revise si están registradas y apruebe o rechace cada una.
            </Typography>

            {reqUsers.map((ru: RequestedUser, idx: number) => {
              const match = matches[ru.cedula];
              const isProcessed = ru.status !== 'pending';

              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: isProcessed ? 'divider' : 'warning.light',
                    borderRadius: 1,
                    bgcolor: isProcessed ? 'action.hover' : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{ru.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Cédula: {ru.cedula}</Typography>
                    </Box>

                    {/* Match status */}
                    {!isProcessed && match && (
                      match.found ? (
                        <Tooltip title={`Encontrado: ${match.user?.firstName} ${match.user?.lastName} (${match.user?.email})`}>
                          <Chip
                            icon={<MatchedIcon sx={{ fontSize: 14 }} />}
                            label="Registrado"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="No se encontró un usuario con esta cédula en la empresa">
                          <Chip
                            icon={<NotFoundIcon sx={{ fontSize: 14 }} />}
                            label="No registrado"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )
                    )}

                    {/* Status chip for processed users */}
                    {isProcessed && (
                      <Chip
                        label={statusLabels[ru.status]}
                        color={statusColors[ru.status]}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    )}

                    {/* Action buttons */}
                    {!isProcessed && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Aprobar">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={responding === idx}
                              onClick={() => handleRespondUser(idx, 'approve')}
                              aria-label="Aprobar"
                            >
                              {responding === idx ? <CircularProgress size={16} /> : <ApproveIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rechazar">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={responding === idx}
                              onClick={() => handleRespondUser(idx, 'reject')}
                              aria-label="Rechazar"
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {/* Info for matched/unmatched after approval */}
                  {ru.status === 'approved' && !ru.matchedUser && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0 }} icon={<RegisterIcon fontSize="small" />}>
                      <Typography variant="caption">
                        Usuario no encontrado — necesita ser registrado en la empresa antes de ser asignado.
                      </Typography>
                    </Alert>
                  )}
                  {ru.status === 'approved' && ru.matchedUser && typeof ru.matchedUser === 'object' && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', pl: 4.5 }}>
                      Mapeado a: {ru.matchedUser.firstName} {ru.matchedUser.lastName} ({ru.matchedUser.email})
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={handleRejectInvitation}
          disabled={acceptingAll}
        >
          Rechazar Invitación
        </Button>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!allProcessed && reqUsers.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Revise todos los usuarios para aceptar
            </Typography>
          )}
          <Button onClick={onClose} color="inherit">Cerrar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAcceptInvitation}
            disabled={acceptingAll || (!allProcessed && reqUsers.length > 0)}
            startIcon={acceptingAll ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
          >
            Aceptar Invitación
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
